import type { Label } from '@/contexts/WorkspaceContext'
import { labelColor } from '@/const/colors'

export interface ResolvedLabel {
    id: string
    name: string
    color: string
}

// A label deleted from the workspace leaves its id behind on every task that carried it —
// deletion doesn't rewrite task docs (that would fire a task.updated webhook for each one).
// So every read path resolves ids against the live label list and drops the ones that no
// longer exist. Order follows the workspace label order, not the order on the task, so the
// same two labels always render in the same sequence across tasks.
export const resolveTaskLabels = (
    labelIds: string[] | null | undefined,
    labels: Label[]
): ResolvedLabel[] => {
    if (!labelIds || labelIds.length === 0) return []
    const ids = new Set(labelIds)
    return labels
        .filter(label => ids.has(label.id))
        .map(label => ({ id: label.id, name: label.name, color: labelColor(label.color) }))
}

// Membership is checked against the raw ids: a task keeps matching a filter for a label that
// exists, and a dead id can never match because no chip can produce it.
export const taskHasLabel = (labelIds: string[] | null | undefined, labelId: string): boolean =>
    !!labelIds && labelIds.includes(labelId)

// One active label at a time today, but the filter is held as a list so multi-select can be
// added without touching call sites. An empty filter means "no filtering".
export const filterTasksByLabels = <T extends { labels?: string[] | null }>(
    tasks: T[],
    activeLabelIds: string[]
): T[] => {
    if (activeLabelIds.length === 0) return tasks
    return tasks.filter(task => activeLabelIds.some(id => taskHasLabel(task.labels, id)))
}

export const toggleLabelId = (labelIds: string[] | null | undefined, labelId: string): string[] => {
    const current = labelIds ?? []
    return current.includes(labelId)
        ? current.filter(id => id !== labelId)
        : [...current, labelId]
}
